import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import SocialLoginButtons from '@/components/SocialLoginButtons';
import BrandWordmark from '@/components/BrandWordmark';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Surface OAuth errors that the backend redirected with (?error=...)
  useEffect(() => {
    const err = searchParams.get('error');
    if (err) {
      const messages = {
        discord_oauth_failed: 'Discord sign-in failed. Please try again.',
        x_oauth_failed: 'X sign-in failed. Please try again.',
        access_denied: 'Sign-in was cancelled.',
      };
      toast.error(messages[err] || `Sign-in error: ${err}`);
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(identifier.trim(), password);
      toast.success('Welcome back');
      // Honor `?next=` for shared-link redirects (e.g. /s/:token).
      const next = searchParams.get('next');
      navigate(next && next.startsWith('/') ? next : '/crate', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="absolute inset-x-0 top-0 h-[40vh] pointer-events-none bg-teal-ink-sheen" aria-hidden="true" />
      <div className="relative w-full max-w-md rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-[0_10px_30px_rgba(0,0,0,0.35)]">
        <Link to="/" className="inline-flex items-center mb-6">
          <BrandWordmark size="md" />
        </Link>

        <h1 className="font-display text-2xl font-semibold mb-1">Welcome back</h1>
        <p className="text-sm text-muted-foreground mb-6">Log in to keep digging.</p>

        <SocialLoginButtons mode="login" />

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="identifier">Email or username</Label>
            <Input
              id="identifier"
              type="text"
              inputMode="email"
              placeholder="email or username"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              required
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              autoComplete="username"
              data-testid="login-email-input"
            />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              data-testid="login-password-input"
            />
          </div>
          <Button
            type="submit"
            disabled={submitting}
            className="w-full rounded-xl"
            data-testid="login-submit-button"
          >
            {submitting ? 'Logging in…' : 'Log in'}
          </Button>
        </form>

        <p className="text-sm text-muted-foreground mt-6">
          New to GroovLens?{' '}
          <Link to="/signup" className="text-[hsl(var(--primary))] hover:underline">
            Create an account
          </Link>
        </p>
      </div>
    </div>
  );
}
